import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'

import OsobaService from '@/services/OsobaService'
import PlacowkaService from '@/services/PlacowkaService'
import RozmowaService from '@/services/RozmowaService'
import PlacowkaFilterModal from '@/filter-modals/components/PlacowkaFilterModal'
import useOsobaModal from '../list/hooks/useOsobaModal'
import OsobaModal from '../list/components/OsobaModal'
import CommunicationHistoryCard from './CommunicationHistoryCard'
import OsobaDetailsCard from './OsobaDetailsCard'
import OsobaPlacowkaCard from './OsobaPlacowkaCard'

const getPlacowkaId = value => {
	if (!value) return null
	if (typeof value === 'object') return value?._id || value?.id || null
	return value
}

const getOsobaIdFromRef = value => {
	if (!value) return null
	if (typeof value === 'object') return value?._id || value?.id || null
	return value
}

export default function OsobaDetails() {
	const { id } = useParams()
	const navigate = useNavigate()
	const [osoba, setOsoba] = useState(null)
	const [placowka, setPlacowka] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [rozmowy, setRozmowy] = useState([])
	const [refreshKey, setRefreshKey] = useState(0)

	const reloadDetails = useCallback(() => {
		setRefreshKey(k => k + 1)
	}, [])

	const {
		isModalOpen,
		editingItem,
		formImieINazwisko,
		setFormImieINazwisko,
		formFunkcja,
		setFormFunkcja,
		formTelefon,
		setFormTelefon,
		formEmail,
		setFormEmail,
		formPlacowkaId,
		formPlacowkaName,
		isSaving,
		isPlacowkaSelectOpen,
		openEditModal,
		closeModal,
		handleSubmit,
		openPlacowkaSelectModal,
		closePlacowkaSelectModal,
		applyPlacowkaSelection,
	} = useOsobaModal(reloadDetails)

	useEffect(() => {
		let alive = true

		const loadDetails = async () => {
			if (!id) {
				setOsoba(null)
				setPlacowka(null)
				setRozmowy([])
				setLoading(false)
				return
			}

			setLoading(true)
			setError(null)
			try {
				const osobaRes = await OsobaService.getById(id)
				if (!alive) return

				if (osobaRes?.error) {
					throw new Error(osobaRes.error)
				}

				const osobaData = osobaRes?.data || null
				setOsoba(osobaData)

				const placowkaId = getPlacowkaId(osobaData?.id_placowka)
				const embeddedPlacowka =
					typeof osobaData?.id_placowka === 'object' ? osobaData.id_placowka : null

				const [placowkaRes, rozmowyRes] = await Promise.all([
					placowkaId ? PlacowkaService.getById(placowkaId) : Promise.resolve({ data: null, error: null }),
					RozmowaService.getByOsobaId(id),
				])

				if (!alive) return

				let resolvedPlacowka = null
				if (!placowkaRes?.error && placowkaRes?.data != null) {
					resolvedPlacowka = placowkaRes.data
				} else if (embeddedPlacowka && getPlacowkaId(embeddedPlacowka)) {
					resolvedPlacowka = embeddedPlacowka
				} else if (placowkaId) {
					resolvedPlacowka = { _id: placowkaId, nazwa: '-', adres: '-' }
				}

				setPlacowka(resolvedPlacowka)

				const rawRozmowy = Array.isArray(rozmowyRes?.data) ? rozmowyRes.data : []
				const filtered = rawRozmowy.filter(
					item => String(getOsobaIdFromRef(item?.id_osoba)) === String(id),
				)
				setRozmowy(filtered.length > 0 ? filtered : rawRozmowy)
			} catch (err) {
				if (!alive) return
				setError(err.message || 'Nie udalo sie zaladowac szczegolow osoby.')
				setOsoba(null)
				setPlacowka(null)
				setRozmowy([])
			} finally {
				if (alive) setLoading(false)
			}
		}

		loadDetails()
		return () => {
			alive = false
		}
	}, [id, refreshKey])

	const handleEditClick = () => {
		if (osoba) openEditModal(osoba)
	}

	const handleDeleteClick = async () => {
		const osobaId = osoba?._id || osoba?.id || id
		if (!osobaId) return

		const displayName =
			osoba?.imie_i_nazwisko || osoba?.imieNazwisko || osoba?.name || '-'

		const confirm = await Swal.fire({
			title: 'Czy na pewno?',
			text: `Usunac osobe "${displayName}"?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Tak, usun',
			cancelButtonText: 'Anuluj',
		})
		if (!confirm.isConfirmed) return

		const result = await OsobaService.delete(osobaId)
		if (!result?.success) {
			Swal.fire({
				title: 'Error',
				text: result?.error || 'Nie udalo sie usunac osoby',
				icon: 'error',
			})
			return
		}

		navigate('/klienci/osoba')
	}

	if (loading) {
		return <div className="min-h-screen flex items-center justify-center">Ladowanie szczegolow osoby...</div>
	}

	if (error) {
		return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>
	}

	if (!id) {
		return <div className="min-h-screen flex items-center justify-center">Nie wybrano osoby.</div>
	}

	return (
		<div>
			<OsobaDetailsCard osoba={osoba} onEdit={handleEditClick} onDelete={handleDeleteClick} />

			<ul role="list" className="grid grid-cols-1 gap-6 xl:grid-cols-2">
				<CommunicationHistoryCard rozmowy={rozmowy} osoba={osoba} placowka={placowka} />
				<OsobaPlacowkaCard placowka={placowka} />
			</ul>

			<OsobaModal
				isOpen={isModalOpen}
				editingItem={editingItem}
				formImieINazwisko={formImieINazwisko}
				setFormImieINazwisko={setFormImieINazwisko}
				formFunkcja={formFunkcja}
				setFormFunkcja={setFormFunkcja}
				formTelefon={formTelefon}
				setFormTelefon={setFormTelefon}
				formEmail={formEmail}
				setFormEmail={setFormEmail}
				formPlacowkaId={formPlacowkaId}
				formPlacowkaName={formPlacowkaName}
				onOpenPlacowkaSelect={openPlacowkaSelectModal}
				isSaving={isSaving}
				onClose={closeModal}
				onSubmit={handleSubmit}
			/>
			<PlacowkaFilterModal
				isOpen={isPlacowkaSelectOpen}
				onClose={closePlacowkaSelectModal}
				selectedPlacowkaId={formPlacowkaId}
				onApply={applyPlacowkaSelection}
				title="Wybierz placowke"
				description="Wyszukaj i wybierz placowke dla osoby."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
		</div>
	)
}
